import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Row, Col, Button, Form, Card } from "react-bootstrap";
import { toast } from "react-toastify";
import Layout from "../../layout/Layout";
import VariantFields from "../../Component/theme-management/VariantFields";
import StatusSwitch from "../../Component/theme-management/StatusSwitch";
import { widgetsApi, variantsApi, themesApi, widgetCategoriesApi, isAuthError, apiErrorMessage } from "../../helpers/tmClient";
import {
  WIDGET_SCREEN_OPTIONS,
  WIDGET_SIZE_OPTIONS,
} from "../../helpers/tmConstants";
import { defaultTextStylesFor, buildTextStylesFormData } from "../../helpers/textStyles";

/** A blank variant working object. */
const blankVariant = (category = "") => ({
  widget_type: "",
  preview_image: null,
  widget_image: null,
  clock_image: null,
  quote: "",
  text_color: "",
  font_name: "",
  alignment: "",
  shape: "",
  status: true,
  text_styles: defaultTextStylesFor(category),
  content_padding: null,
  hour_hand_image: null,
  minute_hand_image: null,
  hand_center_x: null,
  hand_center_y: null,
  // config params
  accent_color: null, bolt_color: null,
});

const CONFIG_FIELDS = ["accent_color", "bolt_color"];

const appendConfigFields = (fd, source) => {
  for (const f of CONFIG_FIELDS) {
    const v = source[f];
    fd.append(f, v === null || v === undefined ? "" : String(v));
  }
};

/**
 * ThemeWidgetAdd — create a widget together with its 1–3 size variants.
 * The widget is created first, then each variant is posted against the
 * new widget id. Duplicate sizes are prevented in the builder itself.
 */
const ThemeWidgetAdd = () => {
  const navigate = useNavigate();
  const [themes, setThemes] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [values, setValues] = useState({
    theme_id: "",
    widget_screen: "",
    widget_name: "",
    category_name: "",
    premium: false,
    status: true,
  });
  const [variants, setVariants] = useState([]);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [tRes, cRes] = await Promise.all([themesApi.options(), widgetCategoriesApi.list()]);
        if (isAuthError(tRes)) return navigate("/");
        setThemes(tRes.data.data || []);
        const docs = cRes.data.data || [];
        const cats = docs.flatMap((d) => (d.get_SubCategory || []).map((s) => ({ value: s.name, label: s.name })));
        setCategoryOptions(cats);
      } catch { /* non-fatal */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setValues((v) => ({ ...v, [name]: type === "checkbox" ? checked : value }));
    setErrors((er) => ({ ...er, [name]: null }));
    // Re-seed default text styles for any variant the user hasn't
    // already customized, so switching category still fills roles in.
    if (name === "category_name") {
      setVariants((vs) =>
        vs.map((v) => (v.text_styles && v.text_styles.length ? v : { ...v, text_styles: defaultTextStylesFor(value) }))
      );
    }
  };
  const onSwitch = (name) => (checked) => setValues((v) => ({ ...v, [name]: checked }));

  /* --------------------- variant builder ops --------------------- */
  const usedSizes = variants.map((v) => v.widget_type).filter(Boolean);
  const addVariant = () => {
    if (variants.length >= WIDGET_SIZE_OPTIONS.length) return;
    setVariants((vs) => [...vs, blankVariant(values.category_name)]);
  };
  const removeVariant = (i) => setVariants((vs) => vs.filter((_, idx) => idx !== i));
  const setVariantField = (i, field, val) =>
    setVariants((vs) => vs.map((v, idx) => (idx === i ? { ...v, [field]: val } : v)));

  const validate = () => {
    const e = {};
    if (!values.widget_screen) e.widget_screen = "Widget screen is required";
    if (!values.widget_name.trim()) e.widget_name = "Widget name is required";
    // Variant-level required checks.
    const seen = new Set();
    variants.forEach((v, i) => {
      if (v.widget_type) {
        if (seen.has(v.widget_type)) e[`v_${i}_widget_type`] = "Duplicate size";
        else seen.add(v.widget_type);
      }
      if (!v.preview_image) e[`v_${i}_preview_image`] = "Required";
      if (!v.widget_image) e[`v_${i}_widget_image`] = "Required";
    });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async () => {
    if (!validate()) {
      toast.error("Please fix the highlighted fields", { position: "bottom-right", autoClose: 1200 });
      return;
    }
    setSaving(true);
    try {
      // 1) Create the widget.
      const res = await widgetsApi.create({
        theme_id: values.theme_id || "",
        widget_screen: values.widget_screen,
        widget_name: values.widget_name,
        category_name: values.category_name || "",
        premium: values.premium,
        status: values.status,
      });
      if (!res.data.status || !res.data.data?._id) {
        if (res.data.errors) setErrors(res.data.errors);
        toast.error(apiErrorMessage(res.data, "Failed to create widget"), { position: "bottom-right", autoClose: 1200 });
        setSaving(false);
        return;
      }
      const widgetId = res.data.data._id;

      // 2) Create each variant against the new widget. All-or-nothing: if
      // any variant fails, stop immediately and roll back everything
      // created so far (the widget + any already-created variants) —
      // the user should never end up with a widget silently missing the
      // variant that failed to save.
      const createdVariantIds = [];
      let failMessage = null;
      for (const v of variants) {
        const fd = new FormData();
        fd.append("widget_id", widgetId);
        fd.append("widget_type", v.widget_type);
        fd.append("quote", v.quote || "");
        fd.append("text_color", v.text_color || "");
        fd.append("font_name", v.font_name || "");
        fd.append("alignment", v.alignment || "");
        fd.append("shape", v.shape || "");
        fd.append("content_padding", v.content_padding || "");
        buildTextStylesFormData(fd, v.text_styles || []);
        fd.append("status", v.status);
        if (v.preview_image) fd.append("preview_image", v.preview_image);
        if (v.widget_image) fd.append("widget_image", v.widget_image);
        if (v.clock_image) fd.append("clock_image", v.clock_image);
        if (v.hand_center_x != null) fd.append("hand_center_x", v.hand_center_x);
        if (v.hand_center_y != null) fd.append("hand_center_y", v.hand_center_y);
        if (v.hour_hand_image) fd.append("hour_hand_image", v.hour_hand_image);
        if (v.minute_hand_image) fd.append("minute_hand_image", v.minute_hand_image);
        appendConfigFields(fd, v);
        const vr = await variantsApi.create(fd);
        if (vr.data.status) {
          if (vr.data.data?._id) createdVariantIds.push(vr.data.data._id);
        } else {
          failMessage = `Variant ${v.widget_type || ""}: ${apiErrorMessage(vr.data, "failed")}`;
          break;
        }
      }

      if (failMessage) {
        for (const id of createdVariantIds) await variantsApi.remove(id).catch(() => {});
        await widgetsApi.remove(widgetId).catch(() => {});
        toast.error(failMessage, { position: "bottom-right", autoClose: 2500 });
        setSaving(false);
        return;
      }

      toast.success(`Widget created${variants.length ? ` with ${variants.length} variant(s)` : ""}`, { position: "bottom-right", autoClose: 900 });
      navigate("/theme-widgets");
    } catch (err) {
      const data = err?.response?.data;
      if (data?.errors) setErrors(data.errors);
      toast.error(apiErrorMessage(data, "Request failed"), { position: "bottom-right", autoClose: 1200 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Layout sidebar={true}>
      <div className="page-heading">
        <h3 className="my-1">Add Widget</h3>
      </div>
      <div className="page-content">
        <Form>
          <Card className="mb-4">
            <Card.Body>
              <fieldset disabled={saving} style={{ border: "none", padding: 0, margin: 0 }}>
              <Row>
                <Col lg={4} sm={12}>
                  <Form.Group>
                    <Form.Label>Theme</Form.Label>
                    <Form.Select name="theme_id" className="my-2" value={values.theme_id} onChange={onChange}>
                      <option value="">None (standalone)</option>
                      {themes.map((t) => (<option key={t._id} value={t._id}>{t.name} ({t.theme_type})</option>))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col lg={4} sm={12}>
                  <Form.Group>
                    <Form.Label>Widget Screen <span className="text-danger">*</span></Form.Label>
                    <Form.Select name="widget_screen" className="my-2" value={values.widget_screen} onChange={onChange} isInvalid={!!errors.widget_screen}>
                      <option value="">Select Screen</option>
                      {WIDGET_SCREEN_OPTIONS.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                    </Form.Select>
                    <Form.Control.Feedback type="invalid">{errors.widget_screen}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col lg={4} sm={12}>
                  <Form.Group>
                    <Form.Label>Widget Name <span className="text-danger">*</span></Form.Label>
                    <Form.Control type="text" name="widget_name" className="my-2" value={values.widget_name} onChange={onChange} isInvalid={!!errors.widget_name} placeholder="Enter widget name" />
                    <Form.Control.Feedback type="invalid">{errors.widget_name}</Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col lg={4} sm={12}>
                  <Form.Group>
                    <Form.Label>Category</Form.Label>
                    <Form.Select name="category_name" className="my-2" value={values.category_name} onChange={onChange}>
                      <option value="">Select Category</option>
                      {categoryOptions.map((o) => (<option key={o.value} value={o.value}>{o.label}</option>))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col lg={3} sm={6}>
                  <Form.Label className="d-block">Premium</Form.Label>
                  <StatusSwitch checked={values.premium} disabled={saving} onChange={onSwitch("premium")} onLabel="ON" offLabel="OFF" />
                </Col>
                <Col lg={3} sm={6}>
                  <Form.Label className="d-block">Active</Form.Label>
                  <StatusSwitch checked={values.status} disabled={saving} onChange={onSwitch("status")} />
                </Col>
              </Row>

              {/* ---------------------- Variant Builder ---------------------- */}
              <hr />
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Widget Sizes (Variants)</h5>
                <Button variant="primary" size="sm" onClick={addVariant} disabled={saving || variants.length >= WIDGET_SIZE_OPTIONS.length}>
                  <i className="bx bx-plus"></i> Add Variant
                </Button>
              </div>
              {variants.length === 0 && (
                <p className="text-muted mt-2 mb-0">No variants yet. Add up to 3 (one per size).</p>
              )}
              {variants.map((v, i) => (
                <VariantFields
                  key={i}
                  title={`Variant ${i + 1}`}
                  value={v}
                  category={values.category_name}
                  usedSizes={usedSizes}
                  disabled={saving}
                  errors={{
                    widget_type: errors[`v_${i}_widget_type`],
                    preview_image: errors[`v_${i}_preview_image`],
                    widget_image: errors[`v_${i}_widget_image`],
                  }}
                  onField={(f, val) => setVariantField(i, f, val)}
                  onFile={(f, file) => setVariantField(i, f, file)}
                  onRemove={() => removeVariant(i)}
                />
              ))}
              </fieldset>
            </Card.Body>
            <Card.Footer className="text-end">
              <Button variant="primary" className="me-3 d-inline-flex align-items-center gap-2" onClick={submit} disabled={saving}>
                {saving && <span className="spinner-border spinner-border-sm" role="status"></span>}
                {saving ? "Saving..." : "Save"}
              </Button>
              <Link to="/theme-widgets"><Button variant="secondary" disabled={saving}>Cancel</Button></Link>
            </Card.Footer>
          </Card>
        </Form>
      </div>
    </Layout>
  );
};

export default ThemeWidgetAdd;
